import { getAllClauses, IClause } from "@/api";
import { addClausesBulkToTopic, ITopicClauseBulkCreate } from "@/api/topics";
import { TableFilterToolbar } from "@/components/admin/table/TableFilterToolbar";
import { Button } from "@/components/shared/buttons/Button";
import { DialogFormContainer } from "@/components/shared/dialogs/DialogFormContainer";
import { DialogMutationError } from "@/components/shared/dialogs/dialog-mutation-states/DialogMutationError";
import { DialogMutationPending } from "@/components/shared/dialogs/dialog-mutation-states/DialogMutationPending";
import { DialogMutationSuccess } from "@/components/shared/dialogs/dialog-mutation-states/DialogMutationSuccess";
import { Table } from "@/components/shared/table/Table";
import { ClauseTypesCell } from "@/components/shared/table/cells/ClauseTypesCell";
import { PolicyTypeCell } from "@/components/shared/table/cells/PolicyTypeCell";
import { useFilterModel } from "@/hooks/useFilterModel";
import { handleRowSelection } from "@/utils";
import {
    Dialog,
    Stack,
    Typography,
    useMediaQuery,
    useTheme,
} from "@mui/material";
import { GridColDef, GridRowSelectionModel } from "@mui/x-data-grid-pro";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { DialogProps } from "@toolpad/core";
import { useEffect, useMemo, useRef, useState } from "react";

const columns: GridColDef<IClause>[] = [
    {
        field: "policy_type",
        headerName: "Policy Type",
        display: "flex",
        renderCell: ({ row }) => (
            <PolicyTypeCell policyType={row.policy_type} />
        ),
    },
    {
        field: "clause_types",
        headerName: "Clause Types",
        display: "flex",
        maxWidth: 200,
        sortable: false,
        renderCell: ({ row }) => (
            <ClauseTypesCell clauseTypes={row.clause_types} />
        ),
    },
    {
        field: "title",
        headerName: "Clause Name",
        flex: 1,
        minWidth: 200,
    },
    {
        field: "description",
        headerName: "Description",
        flex: 1,
        minWidth: 200,
    },
];

interface AddClausesToTopicDialogPayload {
    topicId: number;
}

export const AddClausesToTopicDialog = ({
    open,
    onClose,
    payload,
}: DialogProps<AddClausesToTopicDialogPayload>) => {
    const { topicId } = payload;

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));
    const queryClient = useQueryClient();

    const [selectedClauseIds, setSelectedClauseIds] = useState<number[]>([]);
    const [rows, setRows] = useState<IClause[]>([]);

    const { filterModel, handleFilterModelChange } = useFilterModel();

    const [paginationModel, setPaginationModel] = useState({
        page: 0,
        pageSize: 10,
    });

    const handleRowSelectionModelChange = (
        rowSelectionModel: GridRowSelectionModel
    ) => {
        setSelectedClauseIds(handleRowSelection(rowSelectionModel, rows));
    };

    const {
        mutate,
        isPending: mutationPending,
        error: mutationError,
        isSuccess: mutationSuccess,
        data: mutationData,
    } = useMutation({
        mutationFn: () => {
            const bulkCreate: ITopicClauseBulkCreate = {
                clause_ids: selectedClauseIds,
            };
            return addClausesBulkToTopic(topicId, bulkCreate);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: ["admin-topic-details", String(topicId)],
            });
            queryClient.invalidateQueries({
                queryKey: ["admin-topics"],
            });
        },
    });

    // Fetch clauses from the API
    const { data, isFetching } = useQuery({
        queryKey: [
            "admin-topic-add-clauses",
            paginationModel.page,
            paginationModel.pageSize,
            filterModel,
        ],
        queryFn: () =>
            getAllClauses(
                paginationModel.page + 1,
                paginationModel.pageSize,
                filterModel
            ),
    });

    useEffect(() => {
        if (data) {
            setRows(data.items);
        }
    }, [data]);

    // Memoize rowCount to avoid resetting the page when data is loading
    const rowCountRef = useRef(data?.total || 0);
    const rowCount = useMemo(() => {
        if (data?.total !== undefined) {
            rowCountRef.current = data?.total;
        }
        return rowCountRef.current;
    }, [data?.total]);

    const handleClose = () => {
        onClose();
        setSelectedClauseIds([]);
    };

    const successMessage = mutationData
        ? `Added ${mutationData.added} clause${mutationData.added !== 1 ? "s" : ""}${mutationData.skipped > 0 ? ` (${mutationData.skipped} already in topic)` : ""}.`
        : "Clauses added successfully.";

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="lg"
            fullWidth
            fullScreen={isMobile}
        >
            <DialogFormContainer maxWidth="xl" py={6}>
                <Stack alignItems="center" gap={2} width="100%" marginX="auto">
                    {mutationPending && <DialogMutationPending />}

                    {!mutationError && !mutationSuccess && (
                        <Stack alignItems="center">
                            <Typography variant="h6" fontWeight={600}>
                                Add Clauses to Topic
                            </Typography>
                        </Stack>
                    )}

                    {!mutationPending && !mutationError && !mutationSuccess && (
                        <>
                            <Stack width="100%" height={500} px={6}>
                                <Table
                                    loading={isFetching}
                                    columns={columns}
                                    rows={rows}
                                    checkboxSelection
                                    onRowSelectionModelChange={
                                        handleRowSelectionModelChange
                                    }
                                    pageSizeOptions={[10, 25, 50]}
                                    paginationModel={paginationModel}
                                    rowCount={rowCount}
                                    onPaginationModelChange={setPaginationModel}
                                    paginationMode="server"
                                    filterMode="server"
                                    sortingMode="server"
                                    onFilterModelChange={
                                        handleFilterModelChange
                                    }
                                    onSortModelChange={handleFilterModelChange}
                                    showToolbar
                                    slots={{
                                        toolbar: TableFilterToolbar,
                                    }}
                                />
                            </Stack>

                            <Stack
                                direction="row"
                                gap={3}
                                px={1}
                                maxWidth={379}
                                width="100%"
                                marginX="auto"
                            >
                                <Button
                                    type="button"
                                    variant="outlined"
                                    color="dark"
                                    sx={{ borderRadius: 2, py: 1 }}
                                    fullWidth
                                    onClick={handleClose}
                                    disableElevation
                                >
                                    Cancel
                                </Button>
                                <Button
                                    variant="contained"
                                    color="dark"
                                    sx={{ borderRadius: 2, py: 1 }}
                                    disabled={selectedClauseIds.length === 0}
                                    onClick={() => mutate()}
                                    fullWidth
                                    disableElevation
                                >
                                    Add {selectedClauseIds.length} Clause
                                    {selectedClauseIds.length === 1 ? "" : "s"}
                                </Button>
                            </Stack>
                        </>
                    )}

                    {mutationError && (
                        <DialogMutationError
                            onClose={handleClose}
                            subject="Clauses"
                            description="Failed to add clauses to the topic."
                        />
                    )}

                    {mutationSuccess && (
                        <DialogMutationSuccess
                            onClose={handleClose}
                            description={successMessage}
                        />
                    )}
                </Stack>
            </DialogFormContainer>
        </Dialog>
    );
};
